import {assert} from 'Asserts';
import * as ast from 'notebook/tables/excess/AST';
import {ASTNode, CellLock, RangeLock} from 'notebook/tables/excess/AST';
import {RelativeCellAddress} from 'notebook/tables/excess/EvaluationConstants';
import * as util from 'notebook/tables/excess/Util';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {Optional} from 'util/Types';

/**
 * This is a TypeScript implementation of the spreadsheet tokenizer.
 * The backend tokenizer is found in benchling/notebook/tables/excess/tokenizer.py
 * Please keep the two algorithms in sync by updating both the files and corresponding tests.
 */
export class Token {
  type: TYPES;
  text: string;
  node: ASTNode | null;

  constructor(type: TYPES, text: string, node: Optional<ASTNode> = null) {
    this.type = type;
    this.text = text;
    this.node = node;
    assert(
      (this.node != null) === (this.type === TYPES.AST_NODE),
      'Token has a node if and only if it is an ASTNode.'
    );
  }

  value(): ASTNode {
    assert(this.isASTNode(), 'Only AST node Tokens have a value.');
    return this.node;
  }

  isComma(): this is {type: TYPES.COMMA} {
    return this.type === TYPES.COMMA;
  }

  isLeftParen(): this is {type: TYPES.LEFT_PAREN} {
    return this.type === TYPES.LEFT_PAREN;
  }

  isRightParen(): this is {type: TYPES.RIGHT_PAREN} {
    return this.type === TYPES.RIGHT_PAREN;
  }

  isSpace(): this is {type: TYPES.SPACE} {
    return this.type === TYPES.SPACE;
  }

  isASTNode(): this is {type: TYPES.AST_NODE; node: ASTNode} {
    return this.type === TYPES.AST_NODE;
  }

  isCellRef(): this is {type: TYPES.AST_NODE; node: ast.Cell | ast.CellRange} {
    return this.isASTNode() && (this.node instanceof ast.Cell || this.node instanceof ast.CellRange);
  }
}

function parseRow(row: string): number {
  return parseInt(row, 10) - 1;
}

function parseCol(col: string): number {
  return util.colLettersToNumbers(col);
}

/**
 * Returns the table name corresponding to a match.
 * @param  {Array} match   Array of strings returned by the `exec()` method of a regular expression beginning
 *                         with SHEET_NAME_PATTERN.
 * @return {String}        Table name correponding to match, or null if no table name was found.
 */
function getTableName(match: RegExpExecArray): string | null {
  const {unescapedTable, escapedTable, documentQuoted, documentUnquoted, tableQuoted, tableUnquoted} =
    match.groups || {};
  const unescapedTableName = unescapedTable;
  const escapedTableName = escapedTable != null ? escapedTable.replace(/''/g, "'") : undefined;

  if (documentQuoted != null || documentUnquoted != null) {
    // Return the table part, with proper unescaping for quoted tables
    if (tableQuoted != null) {
      return tableQuoted.replace(/''/g, "'");
    }
    if (tableUnquoted != null) {
      return tableUnquoted;
    }
  }

  return unescapedTableName || escapedTableName || null;
}

/**
 * Returns the document name corresponding to a match.
 * @param  {Array} match   Array of strings returned by the `exec()` method of a regular expression beginning
 *                         with SHEET_NAME_PATTERN.
 * @return {String}        Document name correponding to match, or null if no document name was found.
 */
function getDocName(match: RegExpExecArray): string | null {
  // Handle document-qualified format
  const {documentQuoted, documentUnquoted} = match.groups || {};
  if (documentQuoted != null || documentUnquoted != null) {
    // Return the document part, with proper unescaping for quoted documents
    if (documentQuoted != null) {
      return documentQuoted.replace(/''/g, "'");
    }
    if (documentUnquoted != null) {
      return documentUnquoted;
    }
  }

  return null;
}

/**
 * Parses document-qualified table references in the format 'Document Name'!'Table Name'!A1
 * @param {Array} match Array of strings from regex match
 * @return {Object} Object with document and table names, or nulls if not found
 */
function getDocumentAndTableName(match: RegExpExecArray): {document: string | null; table: string | null} {
  const document = getDocName(match);
  const tableName = getTableName(match);

  return {document, table: tableName};
}

export interface Anchor {
  row: number;
  column: number;
}

export enum TYPES {
  LEFT_PAREN = 'LEFT_PAREN',
  RIGHT_PAREN = 'RIGHT_PAREN',
  COMMA = 'COMMA',
  SPACE = 'SPACE',
  AST_NODE = 'AST_NODE',
}

const QUOTED_NAME_PATTERN = "(?:''|[^'])+";
const UNQUOTED_NAME_PATTERN = '(?!!)\\w+';

// Document name capturing group (quoted OR unquoted)
const DOCUMENT_NAME_GROUP =
  '(?:' +
  `'(?<documentQuoted>${QUOTED_NAME_PATTERN})'` + // Match quoted document name like 'Doc1'
  '|' +
  `(?<documentUnquoted>${UNQUOTED_NAME_PATTERN})` + // OR match unquoted document name like Doc1
  ')!';

// Table name capturing group (quoted OR unquoted)
const TABLE_NAME_GROUP =
  '(?:' +
  `'(?<tableQuoted>${QUOTED_NAME_PATTERN})'` + // Match quoted table name like 'Table1'
  '|' +
  `(?<tableUnquoted>${UNQUOTED_NAME_PATTERN})` + // OR match unquoted table name like Table1
  ')!';

// Simple table patterns (no document qualification)
const SIMPLE_TABLE_GROUP =
  `(?<unescapedTable>${UNQUOTED_NAME_PATTERN})!` + // Match simple unquoted table like Table1!
  '|' +
  `'(?<escapedTable>${QUOTED_NAME_PATTERN})'!`; // Match simple quoted table like 'Table1'!

const SHEET_NAME_PATTERN =
  '(?:' +
  `${DOCUMENT_NAME_GROUP}` + // Document name (quoted or unquoted) + delimiter
  `${TABLE_NAME_GROUP}` + // Table name (quoted or unquoted) + delimiter
  '|' +
  `${SIMPLE_TABLE_GROUP}` + // Simple table names + delimiter
  ')';

const COLUMN_ROW_PATTERN =
  '(?<columnLock>\\$?)' + // capturing group for column lock
  '(?<column>[A-Za-z]+)' + // capturing group for column
  '(?<rowLock>\\$?)' + // capturing group for row lock
  '(?<row>\\d+)'; // capturing group for row

const REL_COLUMN_ROW_PATTERN = `\
R(?:(?<rowAbsolute>-?\\d+)|\\[(?<rowRelative>-?\\d+)\\])\
C(?:(?<colAbsolute>-?\\d+)|\\[(?<colRelative>-?\\d+)\\])\
`;

const CELL_REGEX = new RegExp(`^${SHEET_NAME_PATTERN}?${COLUMN_ROW_PATTERN}`);
const RANGE_REGEX = new RegExp(
  `^${SHEET_NAME_PATTERN}?(?<startColumnLock>\\$?)(?<startColumn>[A-Za-z]+)(?<startRowLock>\\$?)(?<startRow>\\d+):(?<endColumnLock>\\$?)(?<endColumn>[A-Za-z]+)(?<endRowLock>\\$?)(?<endRow>\\d+)`
);

const REL_CELL_REGEX = new RegExp(`^${SHEET_NAME_PATTERN}?${REL_COLUMN_ROW_PATTERN}`);
const REL_RANGE_REGEX = new RegExp(
  `^${SHEET_NAME_PATTERN}?R(?:(?<startRowAbsolute>-?\\d+)|\\[(?<startRowRelative>-?\\d+)\\])C(?:(?<startColAbsolute>-?\\d+)|\\[(?<startColRelative>-?\\d+)\\]):R(?:(?<endRowAbsolute>-?\\d+)|\\[(?<endRowRelative>-?\\d+)\\])C(?:(?<endColAbsolute>-?\\d+)|\\[(?<endColRelative>-?\\d+)\\])`
);

function tokenize(string: string, param?: {relative?: boolean}): Array<Token> {
  if (param == null) {
    param = {};
  }
  let {relative} = param;
  if (relative == null) {
    relative = false;
  }

  const tokens: Array<Token> = [];

  while (string.length > 0) {
    let endId: RelativeCellAddress;
    let id: RelativeCellAddress;
    let locks: Partial<RangeLock> & Partial<CellLock>;
    let node: ast.ASTNode;
    let startId: RelativeCellAddress;
    let token: Token;
    let tokenText: string;
    let value: TypedValue;

    const leftParen = /^\(/.exec(string);
    if (leftParen) {
      tokenText = leftParen[0];
      token = new Token(TYPES.LEFT_PAREN, tokenText);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const rightParen = /^\)/.exec(string);
    if (rightParen) {
      tokenText = rightParen[0];
      token = new Token(TYPES.RIGHT_PAREN, tokenText);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const comma = /^,/.exec(string);
    if (comma) {
      tokenText = comma[0];
      token = new Token(TYPES.COMMA, tokenText);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const space = /^\s+/.exec(string);
    if (space) {
      tokenText = space[0];
      token = new Token(TYPES.SPACE, tokenText);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const func = /^(\w+)\(/.exec(string);
    if (func) {
      tokenText = func[1]; // We want the parenthesis to be the next token
      // @ts-expect-error
      node = new ast.FunctionOperator({type: tokenText.toUpperCase()});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    const operator = /^((<>)|(>=)|(<=)|[+\-/*^><=])/.exec(string);
    if (operator) {
      tokenText = operator[0];
      node = new ast.BinaryOperator({type: tokenText as ast.BinaryOperatorType});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Relative cell range (must be parsed before relative cell)
    const relativeCellRange = REL_RANGE_REGEX.exec(string);
    if (relative && relativeCellRange) {
      const {document, table} = getDocumentAndTableName(relativeCellRange);
      const {
        startRowAbsolute,
        startRowRelative,
        startColAbsolute,
        startColRelative,
        endRowAbsolute,
        endRowRelative,
        endColAbsolute,
        endColRelative,
      } = relativeCellRange.groups || {};

      startId = {
        row: parseInt(startRowAbsolute || startRowRelative, 10),
        column: parseInt(startColAbsolute || startColRelative, 10),
        document,
        table,
      };
      endId = {
        row: parseInt(endRowAbsolute || endRowRelative, 10),
        column: parseInt(endColAbsolute || endColRelative, 10),
        document,
        table,
      };
      locks = {
        startRow: startRowAbsolute != null,
        startCol: startColAbsolute != null,
        endRow: endRowAbsolute != null,
        endCol: endColAbsolute != null,
      };

      tokenText = relativeCellRange[0];
      node = new ast.RelCellRange({start: startId, end: endId, text: tokenText, locks});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Relative cell reference (must be parsed before single cell)
    const relativeCellReference = REL_CELL_REGEX.exec(string);
    if (relative && relativeCellReference) {
      const {document, table} = getDocumentAndTableName(relativeCellReference);
      const {rowAbsolute, rowRelative, colAbsolute, colRelative} = relativeCellReference.groups || {};

      locks = {
        row: rowAbsolute != null,
        col: colAbsolute != null,
      };
      id = {
        row: parseInt(rowAbsolute || rowRelative, 10),
        column: parseInt(colAbsolute || colRelative, 10),
        document,
        table,
      };

      tokenText = relativeCellReference[0];
      node = new ast.RelCell({id, text: tokenText, locks});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Range of cells (must be parsed before single cell)
    const rangeOfCells = RANGE_REGEX.exec(string);
    if (rangeOfCells) {
      const {document, table} = getDocumentAndTableName(rangeOfCells);
      const {
        startColumnLock,
        startColumn,
        startRowLock,
        startRow,
        endColumnLock,
        endColumn,
        endRowLock,
        endRow,
      } = rangeOfCells.groups || {};

      startId = {
        document,
        table,
        row: parseRow(startRow),
        column: parseCol(startColumn),
      };
      endId = {document, table, row: parseRow(endRow), column: parseCol(endColumn)};
      locks = {
        startCol: startColumnLock === '$',
        startRow: startRowLock === '$',
        endCol: endColumnLock === '$',
        endRow: endRowLock === '$',
      };

      tokenText = rangeOfCells[0];
      node = new ast.CellRange({start: startId, end: endId, text: tokenText, locks});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Single cell
    const singleCell = CELL_REGEX.exec(string);
    if (singleCell) {
      const {document, table} = getDocumentAndTableName(singleCell);
      const {columnLock, column, rowLock, row} = singleCell.groups || {};

      id = {
        row: parseRow(row),
        column: parseCol(column),
        document,
        table,
      };
      const cellLocks: ast.CellLock = {
        col: columnLock === '$',
        row: rowLock === '$',
      };
      tokenText = singleCell[0];
      node = new ast.Cell({id, text: tokenText, locks: cellLocks});
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Number
    const numRegEx = new RegExp(`^${util.NUMBER_REGEX}`).exec(string);
    if (numRegEx) {
      value = util.parseValue(numRegEx[0], numRegEx, null);
      tokenText = numRegEx[0];
      node = new ast.Value(value);
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Boolean
    const booleanRegEx = new RegExp(`^${util.BOOLEAN_REGEX.source}`, 'i').exec(string);
    if (booleanRegEx) {
      value = util.parseValue(booleanRegEx[0], null, null, booleanRegEx);
      tokenText = booleanRegEx[0];
      node = new ast.Value(value);
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // String
    const stringRegEx = new RegExp(`^${util.STRING_REGEX}`).exec(string);
    if (stringRegEx) {
      // Replace escaped double quotes with the double quote character.
      const doubleQuoteStr = stringRegEx[1].replace(new RegExp(util.ESCAPED_QUOTE_REGEX, 'g'), '"');
      node = new ast.Value(util.parseValue(doubleQuoteStr, null, stringRegEx));
      tokenText = stringRegEx[0];
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    // Reference Error
    const referenceError = /^#REF!/.exec(string);
    if (referenceError) {
      node = new ast.ReferenceErrorNode();
      tokenText = referenceError[0];
      token = new Token(TYPES.AST_NODE, tokenText, node);
      tokens.push(token);
      string = string.substring(tokenText.length);
      continue;
    }

    throw new util.FormulaTokenizeError(`Found unexpected characters: ${string}`, {
      tokens,
      leftover: string,
    });
  }

  return tokens;
}

function partialTokenize(
  formula: string,
  options: {relative: boolean}
): {
  tokens: Array<Token> | undefined;
  leftover: string | undefined;
} {
  let leftover: string | undefined;
  let tokens: Array<Token> | undefined;
  try {
    tokens = tokenize(formula, options);
    leftover = '';
  } catch (e) {
    if (e instanceof util.FormulaTokenizeError) {
      ({tokens} = e);
      ({leftover} = e);
    } else {
      throw e;
    }
  }
  return {tokens, leftover};
}

// Takes an absolute formula and converts it to a formula relative to the specified cell
function toRelativeFormula(absFormula: string, cell: RelativeCellAddress): string {
  const absFormulaText = util.getFormulaText(absFormula);
  if (absFormulaText == null) {
    return absFormula;
  }

  // Convert tokens to relative formula, and append leftover.
  const {tokens, leftover} = partialTokenize(absFormulaText, {relative: false});
  const relTokens: Array<Token> = [];
  // @ts-expect-error
  tokens.forEach((token) => {
    if (token.isASTNode() && token.node instanceof ast.AbsoluteCellReference) {
      const node = token.node.toRelativeNode(cell);
      relTokens.push(new Token(TYPES.AST_NODE, node.text, node));
    } else {
      relTokens.push(token);
    }
  });

  return `=${relTokens.map((token) => token.text).join('')}${leftover}`;
}

// Takes a formula relative to the specified cell and converts it to an absolute formula
function toAbsoluteFormula(relFormula: string, cell: RelativeCellAddress): string {
  const relFormulaText = util.getFormulaText(relFormula);
  if (relFormulaText == null) {
    return relFormula;
  }

  const {tokens, leftover} = partialTokenize(relFormulaText, {relative: true});

  const absTokens: Array<Token> = [];
  // @ts-expect-error
  tokens.forEach((token) => {
    if (token.isASTNode() && token.node instanceof ast.RelativeCellReference) {
      const node = token.node.toAbsoluteNode(cell);
      absTokens.push(new Token(TYPES.AST_NODE, node.text, node));
    } else {
      absTokens.push(token);
    }
  });

  return `=${absTokens.map((token) => token.text).join('')}${leftover}`;
}

export default {
  tokenize,
  partialTokenize,
  getTableName,
  getDocName,
  toRelativeFormula,
  toAbsoluteFormula,
};
